import { frameBox, selectionPad, type TextMeasure } from './markupBounds.ts';
import { inflateBox } from './markupConstraints.ts';
import { isSegmentShape, type Box, type MarkupShape, type Point } from './markupShapes.ts';
import { shapeHandles, type Handle } from './markupTransforms.ts';

export type OverlayKind = 'selected' | 'hover';

export interface SelectionOverlay {
    frame: Box | null;
    handles: readonly Handle[];
}

type Context2D = CanvasRenderingContext2D;

const ACCENT = '#2563EB';
const UNDERLAY = 'rgba(255, 255, 255, 0.9)';
const HANDLE_SIZE_PX = 9;
const ENDPOINT_RADIUS_PX = 5.5;

// `screenPx` is image pixels per CSS pixel.
export function selectionOverlay(
    shape: MarkupShape,
    measure: TextMeasure,
    screenPx: number
): SelectionOverlay {
    const pad = selectionPad(shape, screenPx);
    const handles = shapeHandles(shape, measure, pad);
    if (isSegmentShape(shape)) return { frame: null, handles };
    return { frame: inflateBox(frameBox(shape, measure), pad), handles };
}

function toScreen(point: Point, scale: number): Point {
    return { x: point.x * scale, y: point.y * scale };
}

// A light underlay keeps the accent line visible on dark and light photos alike.
function strokeTwice(ctx: Context2D, path: () => void, width: number): void {
    ctx.beginPath();
    path();
    ctx.strokeStyle = UNDERLAY;
    ctx.lineWidth = width + 2;
    ctx.stroke();
    ctx.strokeStyle = ACCENT;
    ctx.lineWidth = width;
    ctx.stroke();
}

function drawFrame(ctx: Context2D, frame: Box, scale: number, width: number): void {
    strokeTwice(
        ctx,
        () => ctx.rect(frame.x * scale, frame.y * scale, frame.width * scale, frame.height * scale),
        width
    );
}

function drawHandle(ctx: Context2D, handle: Handle, scale: number): void {
    const at = toScreen(handle.at, scale);
    ctx.beginPath();
    if (handle.id === 'from' || handle.id === 'to')
        ctx.arc(at.x, at.y, ENDPOINT_RADIUS_PX, 0, Math.PI * 2);
    else
        ctx.rect(
            at.x - HANDLE_SIZE_PX / 2,
            at.y - HANDLE_SIZE_PX / 2,
            HANDLE_SIZE_PX,
            HANDLE_SIZE_PX
        );
    ctx.fillStyle = '#FFFFFF';
    ctx.fill();
    ctx.strokeStyle = ACCENT;
    ctx.lineWidth = 1.5;
    ctx.stroke();
}

// Expects a transform of one unit per CSS pixel, so handles keep their size at any zoom.
// Hovering a line shows its ends: an outline drawn along it would hide its colour.
export function drawSelection(
    ctx: Context2D,
    overlay: SelectionOverlay,
    scale: number,
    kind: OverlayKind
): void {
    ctx.save();
    if (overlay.frame) drawFrame(ctx, overlay.frame, scale, kind === 'selected' ? 1.5 : 1);
    if (kind === 'selected' || !overlay.frame)
        overlay.handles.forEach((handle) => drawHandle(ctx, handle, scale));
    ctx.restore();
}
